import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import CollapseHeader from "../../../core/common/collapse-header";
import { AuthContext } from "../../../context/authcontext";
import { useTenantRole } from "../../../components/TenantRoleDisplay";
import { API_BASE_URL } from "../../../environment";
import SettingsTabsNavigation from "../../../components/Tabs/SettingsTabsNavigation";

const ImportData: React.FC = () => {
  const route = all_routes;
  const { selectedTenantId, auth } = useContext(AuthContext);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const userRole = useTenantRole(selectedTenantId || 0);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);

    const fileInput = document.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedTenantId || !auth?.user_id) {
      setUploadError(
        "File, tenant selection, and user authentication required"
      );
      return;
    }

    const userId = auth.user_id;

    setShowModal(true);
    setIsUploading(true);
    setUploadSuccess(false);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append("tenant_id", selectedTenantId.toString());
      formData.append("user_id", userId.toString());
      formData.append("file", selectedFile);

      const authCred = localStorage.getItem("authCred");
      if (!authCred) {
        throw new Error("Authentication required. Please login first.");
      }

      const response = await fetch(
        `${API_BASE_URL}/upload-csv/?tenant_id=${selectedTenantId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Basic ${authCred}`,
          },
          body: formData,
        }
      );

      if (response.ok) {
        const result = await response.json();
        setIsUploading(false);
        setUploadSuccess(true);
      } else {
        let errorMessage = "Upload failed";
        try {
          const errorData = await response.json();
          if (errorData && (errorData.error || errorData.message)) {
            errorMessage = errorData.error || errorData.message;
          }
        } catch (parseErr) {
          errorMessage = `${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Upload error:", error);
      setIsUploading(false);
      setUploadSuccess(false);
      setUploadError(
        error instanceof Error
          ? error.message
          : "Upload failed. Please try again."
      );
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsUploading(false);
    setUploadSuccess(false);
    setUploadError(null);

    if (uploadSuccess) {
      setSelectedFile(null);
      const fileInput = document.querySelector(
        'input[type="file"]'
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col-8">
              <h4 className="page-title">Settings</h4>
            </div>
            <div className="col-4 text-end">
              <div className="head-icons">
                <CollapseHeader />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="card">
          <div className="card-body pb-0 pt-2">
            <SettingsTabsNavigation
              activeTab="import"
              variant="bottom"
              includeIcons={true}
              context="organization"
            />
          </div>
        </div>

        {/* Import Data Content */}
        <div className="row">
          <div className="col-xl-3 col-lg-12 theiaStickySidebar">
            {/* Settings Sidebar */}
            <div className="card">
              <div className="card-body">
                <div className="settings-sidebar">
                  <h4 className="fw-semibold mb-3">Import</h4>
                  <div className="list-group list-group-flush settings-sidebar">
                    <Link to={route.importSettings} className="fw-medium">
                      Import Settings
                    </Link>
                    <Link to={route.importData} className="fw-medium active">
                      Import Data
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            {/* /Settings Sidebar */}
          </div>
          <div className="col-xl-9 col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="border-bottom mb-4 pb-3">
                  <h4 className="fw-semibold mb-1">Import Data</h4>
                  <p className="mb-0">
                    Upload your data from CSV or Excel files
                  </p>
                </div>

                {/* Import Form */}
                <form>
                  {/* Step 2: Upload File */}
                  <div className="mb-4">
                    <h6 className="text-primary fw-semibold mb-3">
                      Upload CSV File
                    </h6>
                    <div className="mb-3">
                      <label className="form-label">Select File</label>
                      <div className="position-relative">
                        <input
                          type="file"
                          className="form-control"
                          accept=".csv"
                          onChange={handleFileSelect}
                          style={{ display: selectedFile ? "none" : "block" }}
                        />
                        {selectedFile && (
                          <div className="form-control d-flex align-items-center justify-content-between">
                            <span className="text-truncate">
                              <i className="ti ti-file me-2"></i>
                              {selectedFile.name}
                            </span>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-danger p-0 ms-2 text-decoration-none"
                              onClick={handleCancel}
                              title="Remove file"
                            >
                              <i className="ti ti-x"></i>
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="form-text">
                        Supported formats: only CSV Maximum file size: 10MB
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleUpload}
                        disabled={!selectedFile || isUploading}
                      >
                        {isUploading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <i className="ti ti-file-upload me-2"></i>
                            Upload
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={handleCancel}
                        disabled={!selectedFile}
                      >
                        <i className="ti ti-x me-2"></i>
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Upload Success Modal */}
        {showModal && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header border-0">
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCloseModal}
                    disabled={isUploading}
                  ></button>
                </div>
                <div className="modal-body text-center py-4">
                  {isUploading ? (
                    <>
                      <div
                        className="spinner-border text-primary mb-3"
                        role="status"
                        style={{ width: "3rem", height: "3rem" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <h5 className="mb-2">Uploading File...</h5>
                      <p className="text-muted">
                        Please wait while we process your file
                      </p>
                    </>
                  ) : uploadSuccess ? (
                    <>
                      <div className="mb-4">
                        <div
                          className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success-subtle mb-3"
                          style={{ width: "80px", height: "80px" }}
                        >
                          <i
                            className="ti ti-check text-success"
                            style={{ fontSize: "2.5rem", fontWeight: "bold" }}
                          ></i>
                        </div>
                      </div>
                      <h5 className="mb-2 text-success fw-bold">
                        File Uploaded Successfully!
                      </h5>
                      <p className="text-muted">
                        Your file <strong>{selectedFile?.name}</strong> has been
                        uploaded and processed.
                      </p>
                      <button
                        type="button"
                        className="btn btn-success mt-3 px-4"
                        onClick={handleCloseModal}
                      >
                        <i className="ti ti-check me-2"></i>
                        Done
                      </button>
                    </>
                  ) : uploadError ? (
                    <>
                      <div className="mb-4">
                        <div
                          className="d-inline-flex align-items-center justify-content-center rounded-circle bg-danger-subtle mb-3"
                          style={{ width: "80px", height: "80px" }}
                        >
                          <i
                            className="ti ti-x text-danger"
                            style={{ fontSize: "2.5rem", fontWeight: "bold" }}
                          ></i>
                        </div>
                      </div>
                      <h5 className="mb-2 text-danger fw-bold">
                        Upload Failed!
                      </h5>
                      <p className="text-muted">{uploadError}</p>
                      <button
                        type="button"
                        className="btn btn-danger mt-3 px-4"
                        onClick={handleCloseModal}
                      >
                        <i className="ti ti-x me-2"></i>
                        Close
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImportData;
